/**
 * RiskProfileClient - Customer Risk Profile Management
 *
 * Provides methods to create, retrieve, and manage customer risk profiles
 * in the Vesant Compliance Platform.
 */

import { BaseClient } from '../core/client';
import { VesantError } from '../core/errors';
import type { BaseClientConfig, RequestOptions } from '../core/config';
import type {
  CustomerProfile,
  CreateProfileRequest,
  UpdateProfileRequest,
} from './types';

export class RiskProfileClient extends BaseClient {
  constructor(config: BaseClientConfig) {
    super(config);
  }

  // ============================================================================
  // Profile Management
  // ============================================================================

  /**
   * Create a new customer risk profile
   *
   * @param request - Profile creation data
   * @returns Created customer profile
   *
   * @example
   * ```typescript
   * const profile = await client.createProfile({
   *   customer_id: 'CUST-12345',
   *   entity_type: 'individual',
   *   customer_status: 'active',
   *   full_name: 'John Doe',
   *   email_address: 'john@example.com',
   *   date_of_birth: '1990-01-15',
   *   country_of_residence: 'US'
   * });
   * ```
   */
  async createProfile(request: CreateProfileRequest, requestOptions?: RequestOptions): Promise<CustomerProfile> {
    return this.request<CustomerProfile>('/api/v1/profiles', {
      method: 'POST',
      body: JSON.stringify(request),
    }, undefined, requestOptions);
  }

  /**
   * Get customer profile by customer ID
   *
   * Note: This searches for the profile using the customer_id field.
   * Returns the first matching profile for the tenant.
   *
   * @param customerId - Customer ID to search for
   * @returns Customer profile
   *
   * @example
   * ```typescript
   * const profile = await client.getProfile('CUST-12345');
   * ```
   */
  async getProfile(customerId: string, requestOptions?: RequestOptions): Promise<CustomerProfile> {
    try {
      // Use the SDK-authenticated endpoint (API key auth)
      return await this.request<CustomerProfile>(
        `/api/v1/profiles?customer_id=${encodeURIComponent(customerId)}`,
        undefined, undefined, requestOptions
      );
    } catch (error) {
      // Normalize to NOT_FOUND for consistent error handling in getOrCreateProfile
      if (error instanceof VesantError && (error.statusCode === 404 || error.code === 'NOT_FOUND')) {
        throw new VesantError('Profile not found', 'NOT_FOUND', 404);
      }
      throw error;
    }
  }

  /**
   * Update customer profile
   *
   * @param profileId - Profile UUID
   * @param updates - Fields to update
   * @returns Updated customer profile
   *
   * @example
   * ```typescript
   * const updated = await client.updateProfile(profileId, {
   *   location: 'New York, USA',
   *   location_compliance: 'compliant',
   *   last_recorded_activity: new Date().toISOString()
   * });
   * ```
   */
  async updateProfile(
    profileId: string,
    updates: UpdateProfileRequest,
    requestOptions?: RequestOptions
  ): Promise<CustomerProfile> {
    return this.request<CustomerProfile>(`/api/v1/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, undefined, requestOptions);
  }

  /**
   * Get or create profile (idempotent operation)
   *
   * Attempts to get existing profile by customer_id, creates if not found.
   *
   * @param customerId - Customer ID
   * @param createRequest - Profile data to use if creating new profile
   * @returns Existing or newly created profile
   *
   * @example
   * ```typescript
   * const profile = await client.getOrCreateProfile('CUST-123', {
   *   customer_id: 'CUST-123',
   *   entity_type: 'individual',
   *   full_name: 'John Doe',
   *   email_address: 'john@example.com'
   * });
   * ```
   */
  async getOrCreateProfile(
    customerId: string,
    createRequest: CreateProfileRequest,
    requestOptions?: RequestOptions
  ): Promise<CustomerProfile> {
    try {
      return await this.getProfile(customerId, requestOptions);
    } catch (error) {
      // Only treat NOT_FOUND as "profile doesn't exist" — re-throw other errors
      if (error instanceof VesantError && error.code === 'NOT_FOUND') {
        return await this.createProfile(createRequest, requestOptions);
      }
      throw error;
    }
  }

}
